import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchProgram } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

const formatDate = (iso) => {
  if (!iso) return "";
  return iso.substring(0, 10).replace(/-/g, "/");
};

export default function ProgramAchievementListPage() {
  const { businessDomainId, businessUnitId, programId, goalId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/programs/${programId}/goals/${goalId}`;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchProgram(programId);
      const foundGoal = (d.goals || [])[Number(goalId)];
      const filtered = (d.achievements || []).filter(
        (a) => a.goal?.indicator?.indicatorName === foundGoal?.indicator?.indicatorName
      );
      setRows(filtered.map((a, i) => ({ id: i, ...a })));
    } catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId, goalId]);

  useEffect(() => { loadData(); }, [loadData]);

  const columns = [
    { field: "indicatorName", headerName: t("program.goal.indicator"), flex: 1, minWidth: 150,
      valueGetter: (v, row) => row?.goal?.indicator?.indicatorName || "" },
    { field: "plannedValue", headerName: t("program.achievement.plannedValue"), width: 110,
      valueGetter: (v, row) => row?.goal?.plannedValue || "" },
    { field: "actualValue", headerName: t("program.achievement.actualValue"), width: 110 },
    { field: "unit", headerName: t("program.goal.unit"), width: 80,
      valueGetter: (v, row) => row?.goal?.indicator?.unit || "" },
    { field: "actualDate", headerName: t("program.achievement.actualDate"), width: 120, valueFormatter: (v) => formatDate(v) },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("program.achievement.list.title")}</Typography>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => navigate(`${basePath}/achievements/create`)}>{t("common.add")}</Button>
      </Box>
      <DataGrid
        rows={rows} columns={columns} getRowId={(r) => r.id}
        loading={loading} autoHeight pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        onRowClick={(p) => navigate(`${basePath}/achievements/${p.row.id}/edit`)}
        disableRowSelectionOnClick sx={{ cursor: "pointer" }}
      />
      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => navigate(basePath)}>{t("common.back")}</Button>
      </Box>
    </Box>
  );
}
